import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { cn } from "@/utils/cn";

const Header = () => {
  const location = useLocation();
  const { i18n, t } = useTranslation("common");
  const { setLanguage } = useStore();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHomePage, setIsHomePage] = useState(false);
  const [, forceUpdate] = useState(0); // لإعادة رسم عند تغيير اللغة



useEffect(() => {
  if (location.state?.scrollTo) {
    const section = document.getElementById(location.state.scrollTo);
    if (section) {
      setTimeout(() => {
        section.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }
}, [location.state]);

  useEffect(() => {
    const handleLanguageChange = () => forceUpdate((prev) => prev + 1);
    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  const currentLanguage = i18n.language; // استخدم i18n.language للتحديث الفوري

  useEffect(() => {
    setIsHomePage(location.pathname === "/");
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigation = [
    { key: "nav.home", href: "/" },
    { key: "nav.about", href: "/#about" },
    { key: "nav.sectors", href: "/sectors" },
    { key: "nav.careers", href: "/careers" },
    { key: "nav.investors", href: "/investors" },
    { key: "nav.contact", href: "/contact" },
    { key: "nav.guide", href: "/guide" },
  ];

  const languages = [
    { code: "ar", name: "العربية", flag: "ar" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
  ];

  const organizationNames = {
    ar: "NSPO",
    en: "NSPO",
    fr: "NSPO",
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    i18n.changeLanguage(langCode);
    // Reload page to apply language changes
    window.location.reload();
  };

  const isActive = (path) => location.pathname === path;

  const isTransparent = isHomePage && !scrolled;

  const handleGuideClick = () => {
    const pdfMap = {
      ar: "/pdf/arabic.pdf",
      en: "/pdf/english.pdf",
      fr: "/pdf/french.pdf",
    };

    const pdfPath = pdfMap[currentLanguage] || pdfMap.ar;
    const link = document.createElement("a");
    link.href = pdfPath;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <header
      className={cn(
        "fixed top-0 w-full z-50 transition-all duration-500 border-b",
        isTransparent
          ? "bg-transparent border-transparent"
          : scrolled
          ? "bg-white shadow-lg border-gray-100 dark:bg-gray-900 dark:border-gray-800"
          : "bg-white/95 backdrop-blur-sm border-gray-100 dark:bg-gray-900/95 dark:border-gray-800"
      )}
      dir={currentLanguage === "ar" ? "rtl" : "ltr"}
    >
      <nav className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo Section */}
          <Link
            to="/"
            className="flex items-center space-x-4 rtl:space-x-reverse group"
          >
            <div className="relative">
              <div
                className={cn(
                  "w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300",
                  isTransparent
                    ? "bg-white/20 backdrop-blur-sm border border-white/30"
                    : "bg-gradient-to-br from-blue-50 to-gray-50 border border-gray-200"
                )}
              >
                <img
                  src="/assets/images/NSPO-Logo.webp"
                  alt="NSPO"
                  className="w-10 h-10 object-contain"
                />
              </div>
            </div>

            <div className="flex flex-col">
              <span
                className={cn(
                  "text-xl font-bold leading-tight transition-colors duration-300",
                  isTransparent ? "text-white" : "text-gray-900"
                )}
              >
                {organizationNames[currentLanguage] || organizationNames.ar}
              </span>
              <span
                className={cn(
                  "text-xs font-medium tracking-wider uppercase mt-1 transition-colors duration-300",
                  isTransparent ? "text-white/80" : "text-gray-500"
                )}
              >
                جهاز مشروعات الخدمة الوطنية
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1 rtl:space-x-reverse">
            {navigation.map((item) => (
              <div key={item.key}>
                {item.key === "nav.about" ? (
                  // ⭐ خاص بنبذة عنا
                  <Link
                    to="/"
                    state={{ scrollTo: "about" }}
                    className={cn(
                      "relative px-6 py-3 text-sm font-medium rounded-xl transition-all duration-300 group",
                      isTransparent
                        ? "text-white/90 hover:text-white hover:bg-white/10"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    {t(item.key)}
                  </Link>
                ) : item.href ? (
                  // باقي الروابط
                  <Link
                    to={item.href}
                    className={cn(
                      "relative px-6 py-3 text-sm font-medium rounded-xl transition-all duration-300 group",
                      isTransparent
                        ? isActive(item.href)
                          ? "text-white bg-white/20 backdrop-blur-sm"
                          : "text-white/90 hover:text-white hover:bg-white/10"
                        : isActive(item.href)
                        ? "text-blue-600 bg-blue-50"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    {t(item.key)}

                    {isActive(item.href) && !isTransparent && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-blue-600 rounded-full" />
                    )}
                  </Link>
                ) : (
                  // زر الـ Guide
                  <button
                    type="button"
                    onClick={handleGuideClick}
                    className={cn(
                      "relative px-6 py-3 text-sm font-medium rounded-xl transition-all duration-300 group",
                      isTransparent
                        ? "text-white/90 hover:text-white hover:bg-white/10"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    {t(item.key)}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            {/* Language Selector */}
            <div className="relative">
              <button
                className={cn(
                  "flex items-center space-x-2 rtl:space-x-reverse px-4 py-2.5 rounded-xl border transition-all duration-300 shadow-sm hover:shadow-md",
                  isTransparent
                    ? "bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30"
                    : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                )}
                onClick={() => {
                  const langMenu = document.getElementById("language-menu");
                  langMenu.classList.toggle("hidden");
                }}
              >
                <span className="text-lg">
                  {
                    languages.find((lang) => lang.code === currentLanguage)
                      ?.flag
                  }
                </span>
                <span className="text-sm font-medium">
                  {
                    languages.find((lang) => lang.code === currentLanguage)
                      ?.name
                  }
                </span>
                <svg
                  className="h-4 w-4 transition-transform duration-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <div
                id="language-menu"
                className="hidden absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-48 origin-top-right rounded-xl bg-white shadow-xl border border-gray-200 focus:outline-none overflow-hidden z-50"
              >
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 mb-1">
                    {t("common.language") || "Language"}
                  </div>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        handleLanguageChange(lang.code);
                        document
                          .getElementById("language-menu")
                          .classList.add("hidden");
                      }}
                      className={cn(
                        "flex items-center space-x-3 rtl:space-x-reverse w-full px-3 py-3 text-sm rounded-lg transition-all duration-200",
                        currentLanguage === lang.code
                          ? "bg-blue-50 text-blue-600"
                          : "hover:bg-gray-50"
                      )}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="font-medium flex-1 text-left rtl:text-right">
                        {lang.name}
                      </span>
                      {currentLanguage === lang.code && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              aria-label={mobileMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
              type="button"
              className={cn(
                "lg:hidden flex items-center justify-center w-12 h-12 rounded-xl border transition-all duration-300 shadow-sm hover:shadow-md",
                isTransparent
                  ? "bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              )}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div
            className={cn(
              "lg:hidden rounded-2xl shadow-xl border mt-2 mb-4 overflow-hidden backdrop-blur-lg",
              isTransparent
                ? "bg-white/20 border-white/30"
                : "bg-white border-gray-200"
            )}
          >
            <div className="py-4">
              {navigation.map((item) => (
                <div key={item.key}>
                  {item.href ? (
                    <Link
                      to={item.href.startsWith("#") ? "/" : item.href}
                      onClick={(e) => {
                        if (item.href.startsWith("#")) {
                          e.preventDefault();
                          const section = document.querySelector(item.href);
                          if (section) {
                            section.scrollIntoView({ behavior: "smooth" });
                          }
                        }
                        setMobileMenuOpen(false);
                      }}
                      className={cn(
                        "block px-6 py-4 text-base font-medium transition-all duration-200 border-l-3 rtl:border-l-0 rtl:border-r-3",
                        isTransparent
                          ? isActive(item.href)
                            ? "bg-white/30 text-white border-white"
                            : "text-white/90 hover:bg-white/20 border-transparent"
                          : isActive(item.href)
                          ? "bg-blue-50 text-blue-600 border-blue-500"
                          : "text-gray-600 hover:bg-gray-50 border-transparent"
                      )}
                    >
                      {t(item.key)}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        handleGuideClick();
                        setMobileMenuOpen(false);
                      }}
                      className={cn(
                        "block w-full text-left px-6 py-4 text-base font-medium transition-all duration-200 border-l-3 rtl:border-l-0 rtl:border-r-3",
                        isTransparent
                          ? "text-white/90 hover:bg-white/20 border-transparent"
                          : "text-gray-600 hover:bg-gray-50 border-transparent"
                      )}
                    >
                      {t(item.key)}
                    </button>
                  )}
                </div>
              ))}

              {/* Language Selector in Mobile Menu */}
              <div
                className={cn(
                  "border-t mt-2",
                  isTransparent ? "border-white/20" : "border-gray-200"
                )}
              >
                <div
                  className={cn(
                    "px-6 py-2 text-xs font-semibold uppercase tracking-wide",
                    isTransparent ? "text-white/70" : "text-gray-500"
                  )}
                >
                  {t("common.language") || "Language"}
                </div>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      handleLanguageChange(lang.code);
                      setMobileMenuOpen(false);
                    }}
                    className={cn(
                      "flex items-center space-x-3 rtl:space-x-reverse w-full px-6 py-3 text-sm transition-all duration-200",
                      isTransparent
                        ? currentLanguage === lang.code
                          ? "bg-white/30 text-white"
                          : "text-white/90 hover:bg-white/20"
                        : currentLanguage === lang.code
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
